/**
 * customerPriceApi — Müşteriye özel fiyat (CustomerPrice) upsert'i için
 * TEK yazma noktası.
 *
 * Aynı upsert mantığı daha önce 4 yerde kopyalıydı (CustomerPriceManager,
 * OrderForm, AgentOrders, AdminOrders) ve alanlar arasında ufak farklar
 * drift yaratıyordu. Kurallar:
 *   - (lead_id, product_id) benzersizdir; önce TAZE DB sorgusu yapılır
 *     (bayat cache ile mükerrer kayıt oluşmasın).
 *   - `preis` DB'de NOT NULL — her yazımda `custom_price` ile aynı değer.
 *   - `original_price` yalnızca CREATE'te set edilir (liste fiyatı anlık
 *     görüntüsü); UPDATE'te dokunulmaz.
 */
import { base44 } from '@/api/base44Client';

/**
 * Tek bir müşteri fiyatını upsert eder.
 * @param {{
 *   leadId: string,
 *   leadName?: string,
 *   productId: string,
 *   productName?: string,
 *   productSku?: string,
 *   price: number,            // yeni müşteri fiyatı
 *   originalPrice?: number,   // liste fiyatı (yalnızca create'te kullanılır)
 * }} args
 * @returns {Promise<object>} yazılan CustomerPrice kaydı
 */
export async function upsertCustomerPrice({
  leadId,
  leadName = '',
  productId,
  productName = '',
  productSku = '',
  price,
  originalPrice,
}) {
  if (!leadId || !productId || price == null) {
    throw new Error('upsertCustomerPrice: leadId, productId und price sind erforderlich');
  }

  const found = await base44.entities.CustomerPrice.filter({
    lead_id: leadId,
    product_id: productId,
  });

  if (found && found.length > 0) {
    return base44.entities.CustomerPrice.update(found[0].id, {
      preis: price,
      custom_price: price,
    });
  }

  return base44.entities.CustomerPrice.create({
    lead_id: leadId,
    lead_name: leadName,
    product_id: productId,
    product_name: productName,
    product_sku: productSku,
    original_price: originalPrice ?? price,
    preis: price,
    custom_price: price,
  });
}

/**
 * Bestehenden Kundenpreis per id aktualisieren (Produkt-Editor, Admin).
 * Schreibt `preis` und `custom_price` immer gemeinsam; legt nie an und
 * löscht nie — original_price sowie Lead-/Produktfelder bleiben unberührt.
 * @param {string} id   customer_prices.id
 * @param {number} price neuer Kundenpreis (>= 0)
 */
export async function updateCustomerPriceById(id, price) {
  if (!id) throw new Error('updateCustomerPriceById: id fehlt');
  const value = Number(price);
  if (!Number.isFinite(value) || value < 0) {
    throw new Error('updateCustomerPriceById: Preis muss eine Zahl >= 0 sein');
  }
  return base44.entities.CustomerPrice.update(id, { preis: value, custom_price: value });
}

/**
 * Sipariş kalemlerinden müşteri fiyatlarını toplu upsert eder.
 * Hatalı kalemler işlemi durdurmaz; sonuçta hata listesi döner.
 *
 * @param {{
 *   leadId: string,
 *   leadName?: string,
 *   items: Array<{ product_id?: string, product_name?: string, product_sku?: string,
 *                  unit_price?: number, base_unit_price?: number }>,
 * }} args
 * @returns {Promise<{ saved: number, errors: Array<{ productName: string, message: string }> }>}
 */
export async function upsertCustomerPricesFromItems({ leadId, leadName = '', items }) {
  const result = { saved: 0, errors: [] };
  if (!leadId || !Array.isArray(items)) return result;

  for (const item of items) {
    if (!item.product_id || item.unit_price == null) continue;
    try {
      await upsertCustomerPrice({
        leadId,
        leadName,
        productId: item.product_id,
        productName: item.product_name || '',
        productSku: item.product_sku || '',
        price: item.unit_price,
        originalPrice: item.base_unit_price,
      });
      result.saved++;
    } catch (err) {
      result.errors.push({ productName: item.product_name || item.product_id, message: err.message });
    }
  }
  return result;
}

// ── Fiyat geçmişi (tarih damgalı) ─────────────────────────────

import { getOrderItems } from '@/lib/orderFields';

/**
 * Müşterinin geçmiş siparişlerinden ürün bazlı fiyat geçmişi çıkarır.
 * Yalnızca gerçek siparişler (Bestellung, iptal edilmemiş) sayılır.
 *
 * @param {Array} orders — müşterinin siparişleri
 * @returns {Map<string, Array<{date: string, price: number, orderNo: string, quantity: number}>>}
 *          product_id → tarih damgalı fiyat listesi (yeniden eskiye)
 */
export function buildPriceHistory(orders) {
  const history = new Map();
  (orders || [])
    .filter((o) => o.status !== 'Storniert' && (o.order_type || '').toLowerCase() !== 'angebot')
    .forEach((order) => {
      const date = order.created_date || order.created_at;
      getOrderItems(order).forEach((item) => {
        if (!item.product_id || item.unit_price == null) return;
        const list = history.get(item.product_id) || [];
        list.push({
          date,
          price: Number(item.unit_price),
          orderNo: order.order_number || '',
          quantity: Number(item.quantity) || 0,
        });
        history.set(item.product_id, list);
      });
    });
  history.forEach((list) => list.sort((a, b) => new Date(b.date) - new Date(a.date)));
  return history;
}

/** Ürünün bu müşteriye en son satış kaydı (yoksa null). */
export function lastPurchase(historyMap, productId) {
  const list = historyMap?.get(productId);
  return list && list.length > 0 ? list[0] : null;
}
